import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import { Subject, Subscription, throttleTime } from 'rxjs'
import { MatDialog } from '@angular/material/dialog'
import {
  Card,
  PlayingGame,
  playingGameNull,
  PlayingGameService,
  Trick,
} from 'game-services'
import { DialogComponent, DialogComponentModel } from 'shared-components'
import {
  DashboardComponent,
  DashboardUserAction,
} from '../dashboard/dashboard.component'
import { PlayTrickContent } from '../../contents/play-trick.content'
import { playerScoreCardNull } from 'game-play-services'
import { ConfirmDeclarerHasRestOfTricksDialogComponent } from '../confirm-declarer-has-rest-of-tricks-dialog/confirm-declarer-has-rest-of-tricks-dialog.component'
import { ConfirmDeclarerHasRestOfTricksDialogModel } from '../confirm-declarer-has-rest-of-tricks-dialog/confirm-declarer-has-rest-of-tricks-dialog.model'
import { ConfirmDeclarerHasNoneOfTricksDialogComponent } from '../confirm-declarer-has-none-of-tricks-dialog/confirm-declarer-has-none-of-tricks-dialog.component'
import { ConfirmDeclarerHasNoneOfTricksDialogModel } from '../confirm-declarer-has-none-of-tricks-dialog/confirm-declarer-has-none-of-tricks-dialog.model'

@Component({
  selector: 'sans-playing-game',
  standalone: true,
  imports: [DashboardComponent],
  templateUrl: './playing-game.component.html',
  styleUrl: './playing-game.component.scss',
})
export class PlayingGameComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public playingGame: PlayingGame = playingGameNull
  @Input() public userPlayerScoreCard = playerScoreCardNull
  @Input() public leftOpponentPlayerScoreCard = playerScoreCardNull
  @Input() public rightOpponentPlayerScoreCard = playerScoreCardNull
  @Output() public gameUpdated = new EventEmitter<void>()

  public restOfTricksAreMineLabel = $localize`:Playing game component|Rest of tricks are mine label:All tricks`
  public noneOfTricksAreMineLabel = $localize`:Playing game component|No tricks are mine label:No tricks`

  private restOfTricksAreMineValue = 'restOfTricksAreMineValue'
  private noneOfTricksAreMineValue = 'noneOfTricksAreMineValue'

  public playTrickClicks = new Subject<Card>()
  public playTrickClicksSubscription = new Subscription()

  public restOfTricksAreMineClicks = new Subject<void>()
  public noneOfTricksAreMineClicks = new Subject<void>()
  public restOfTricksAreMineClicksSubscription = new Subscription()
  public noneOfTricksAreMineClicksSubscription = new Subscription()
  private confirmDeclarerHasRestOfTricksDialogSubscription = new Subscription()
  private confirmDeclarerHasNoneOfTricksDialogSubscription = new Subscription()

  public talonCards: Card[] = []
  public userCards: Card[] = []
  public rightOpponentCards: Card[] = []
  public leftOpponentCards: Card[] = []
  public isUserCardsSelectable = false
  public userActions: DashboardUserAction[] = []

  private isConfirmDeclarerHasRestOfTricksDialogOpen = false
  private isConfirmDeclarerHasNoneOfTricksDialogOpen = false

  public constructor(
    private readonly playingGameService: PlayingGameService,
    private readonly dialog: MatDialog,
    private readonly playTrickContent: PlayTrickContent,
  ) {}

  public async ngOnInit(): Promise<void> {
    this.playTrickClicksSubscription = this.playTrickClicks
      .pipe(throttleTime(1000))
      .subscribe(async (card) => {
        await this.handlePlayTrick(card)
      })

    this.restOfTricksAreMineClicksSubscription = this.restOfTricksAreMineClicks
      .pipe(throttleTime(1000))
      .subscribe(async () => {
        await this.handleDeclareRestOfTricksAreMine()
      })

    this.noneOfTricksAreMineClicksSubscription = this.noneOfTricksAreMineClicks
      .pipe(throttleTime(1000))
      .subscribe(async () => {
        await this.handleDeclareNoneOfTricksAreMine()
      })
  }

  public ngOnChanges(): void {
    this.talonCards = this.playingGame.getTalonCards()
    this.isUserCardsSelectable = this.playingGame.getIsUserCardsSelectable()

    this.userCards = this.playingGame.user.getCards()
    this.rightOpponentCards = this.playingGame.rightOpponent.getCards()
    this.leftOpponentCards = this.playingGame.leftOpponent.getCards()

    this.handleConfirmDeclarerHasRestOfTricksDialog()
    this.handleConfirmDeclarerHasNoneOfTricksDialog()

    if (this.playingGame.shouldShowRestOfTricksAreMine) {
      this.userActions = [
        {
          buttonLabel: this.restOfTricksAreMineLabel,
          value: this.restOfTricksAreMineValue,
        },
      ]
    }

    if (this.playingGame.shouldShowNoneOfTricksAreMine) {
      this.userActions = [
        {
          buttonLabel: this.noneOfTricksAreMineLabel,
          value: this.noneOfTricksAreMineValue,
        },
      ]
    }
  }

  public ngOnDestroy(): void {
    this.playTrickClicksSubscription.unsubscribe()
    this.confirmDeclarerHasRestOfTricksDialogSubscription.unsubscribe()
    this.confirmDeclarerHasNoneOfTricksDialogSubscription.unsubscribe()
  }

  public async onPlayTrick(card: Card): Promise<void> {
    this.playTrickClicks.next(card)
  }

  public async onUserActionSelected(
    userAction: DashboardUserAction,
  ): Promise<void> {
    if (userAction.value === this.restOfTricksAreMineValue) {
      this.restOfTricksAreMineClicks.next()
    }

    if (userAction.value === this.noneOfTricksAreMineValue) {
      this.noneOfTricksAreMineClicks.next()
    }
  }

  private async handlePlayTrick(card: Card): Promise<void> {
    const playedTrick: Trick = {
      userId: this.playingGame.user.userId,
      trickRoundNumber: this.playingGame.currentTrickRound,
      card,
    }

    const playTrickResponse = await this.playingGameService.playTrick(
      this.playingGame.gamePlayGameId,
      playedTrick,
    )

    if (
      !playTrickResponse.isSuccessful &&
      playTrickResponse.errorCode === 'InvalidTrick'
    ) {
      this.dialog.open<DialogComponent, DialogComponentModel>(DialogComponent, {
        data: {
          isSuccessful: playTrickResponse.isSuccessful,
          errorDescriptions: this.playTrickContent.getErrorCodeDescriptions(
            playedTrick,
            playTrickResponse.errorCodes,
          ),
          successfulTitle: '',
          unsuccessfulTitle: this.playTrickContent.errorTitle,
        },
      })
    }

    this.gameUpdated.emit()
  }

  private async handleDeclareRestOfTricksAreMine(): Promise<void> {
    await this.playingGameService.declareRestOfTricksAreMine(
      this.playingGame.gamePlayGameId,
    )

    this.gameUpdated.emit()
  }

  private async handleDeclareNoneOfTricksAreMine(): Promise<void> {
    await this.playingGameService.declareNoneOfTricksAreMine(
      this.playingGame.gamePlayGameId,
    )

    this.gameUpdated.emit()
  }

  private handleConfirmDeclarerHasRestOfTricksDialog(): void {
    if (
      !this.isConfirmDeclarerHasRestOfTricksDialogOpen &&
      this.playingGame.shouldShowConfirmDeclarerHasRestOfTricks
    ) {
      this.isConfirmDeclarerHasRestOfTricksDialogOpen = true

      const dialog = this.dialog.open<
        ConfirmDeclarerHasRestOfTricksDialogComponent,
        ConfirmDeclarerHasRestOfTricksDialogModel
      >(ConfirmDeclarerHasRestOfTricksDialogComponent, {
        data: {
          declarerUsername: this.playingGame.getDeclarerUsername(),
          gamePlayGameId: this.playingGame.gamePlayGameId,
        },
        hasBackdrop: false,
        disableClose: true,
      })

      this.confirmDeclarerHasRestOfTricksDialogSubscription = dialog
        .afterClosed()
        .subscribe(() => {
          this.isConfirmDeclarerHasRestOfTricksDialogOpen = false
          this.confirmDeclarerHasRestOfTricksDialogSubscription.unsubscribe()
          this.gameUpdated.emit()
        })
    }
  }

  private handleConfirmDeclarerHasNoneOfTricksDialog(): void {
    if (
      !this.isConfirmDeclarerHasNoneOfTricksDialogOpen &&
      this.playingGame.shouldShowConfirmDeclarerHasNoneOfTricks
    ) {
      this.isConfirmDeclarerHasNoneOfTricksDialogOpen = true

      const dialog = this.dialog.open<
        ConfirmDeclarerHasNoneOfTricksDialogComponent,
        ConfirmDeclarerHasNoneOfTricksDialogModel
      >(ConfirmDeclarerHasNoneOfTricksDialogComponent, {
        data: {
          declarerUsername: this.playingGame.getDeclarerUsername(),
          gamePlayGameId: this.playingGame.gamePlayGameId,
        },
        hasBackdrop: false,
        disableClose: true,
      })

      this.confirmDeclarerHasNoneOfTricksDialogSubscription = dialog
        .afterClosed()
        .subscribe(() => {
          this.isConfirmDeclarerHasNoneOfTricksDialogOpen = false
          this.confirmDeclarerHasNoneOfTricksDialogSubscription.unsubscribe()
          this.gameUpdated.emit()
        })
    }
  }
}
